///////////////////////////////////////////////////////////////////////////////////
//
// Shared {{parameter}} field lists + fill logic for meeting summaries
// (templates AND actual summaries) and for the summon ("זימון") popup.
//
// The server has its own engines for the same tokens:
//   - SummonsService.fillTemplateFields  (per-recipient, gets display values in the POST)
//   - CustomersService.fillTemplateFields (per-recipient, the send-message modal)
//   - AutomationMarkersService.templateToText
// Keep the Hebrew field names aligned across all of them.
//
///////////////////////////////////////////////////////////////////////////////////

import Config from '../Config';
import axiosV2 from '../core/http/axios_api_v2';


export default {


    //just the first / last name of a contact. Where the message goes to a contact (a summon, the
    //send-message modal, the chat, an automation to the customer's contacts) it is THAT contact's
    //name; where there is no single recipient (a document, a summary, an automation to a fixed
    //number / the crew) it is the customer's first contact. Same names in every server engine.
    contactFirstNameField: 'שם פרטי של איש הקשר',
    contactLastNameField: 'שם משפחה של איש הקשר',


    //fields offered when composing a summon (זימון) message or a message template.
    //'שם איש הקשר' is per-recipient, so these are replaced on the server (SummonsService).
    //'סוג הפגישה' is the type alone - the meeting's name usually carries the customer's name
    //("פגישת יעוץ עם דנה"), which reads wrong in a message to Dana herself
    summonMeetingFields: [
        'שם איש הקשר',
        'שם פרטי של איש הקשר',
        'שם משפחה של איש הקשר',
        'שם הפגישה',
        'סוג הפגישה',
        'מקום הפגישה',
        'תאריך הפגישה',
        'יום הפגישה בשבוע',
        'שעת התחלת הפגישה',
        'שעת סיום הפגישה',
    ],


    //fields of the meeting itself, for meeting summaries
    meetingFields: [
        'שם הפגישה',
        'סוג הפגישה',
        'מקום הפגישה',
        'תאריך הפגישה',
        'יום הפגישה בשבוע',
        'שעת התחלת הפגישה',
        'שעת סיום הפגישה',
        'שמות אנשי הקשר בפגישה',
        'שמות אנשי הצוות בפגישה',
    ],


    //fields of the event the meeting belongs to
    eventFields: [
        'שם האירוע',
        'סוג האירוע',
        'מקום האירוע',
        'תאריך האירוע',
        'יום האירוע בשבוע',
        'שעת התחלת האירוע',
        'שעת סיום האירוע',
        'מספר אורחים באירוע',
        'שמות אנשי הקשר באירוע',
        'שמות אנשי הצוות באירוע',
    ],


    //fields of the customer the document/summary is created for (documents, terms, summaries)
    customerFields: [
        'שם הלקוח',
        'שם פרטי של איש הקשר',
        'שם משפחה של איש הקשר',
        'טלפון הלקוח',
        'אימייל הלקוח',
    ],


    //fields of the vendor a message goes to (ספקים נוספים → שליחת מסמך). per recipient, so they are
    //filled on the server (DocumentsService.sendToVendors - keep the names aligned with it);
    //'שם איש הקשר' there is the vendor's contact person, or the vendor itself when it has none
    vendorFields: [
        'שם איש הקשר',
        'שם הספק',
        'איש הקשר של הספק',
        'קטגוריית הספק',
    ],


    //context-free fields, resolve everywhere
    generalFields: [
        'התאריך של היום',
    ],


    //the public booking link ("קישור לזימון פגישה"). context-free like generalFields, but only
    //offered when the system admin turned online booking on for this account (biz-services id 10)
    bookingFields: [
        'קישור לזימון פגישה',
    ],


    //the meeting's own link for the customer (planup.co.il/r/...): one page with אישור הגעה / שינוי מועד /
    //ביטול, or straight to one of them. Signed on the server, so they are filled there (automations on a
    //meeting, the summon, the send-message modal - booking/meeting-response-link.ts MEETING_LINK_PARAMS, keep
    //the names aligned) or fetched from it (meetingLinksOf - the chat). Not tied to online booking: the
    //confirmation and the cancellation work for every account; a new date is picked from free slots when
    //the online booking is on, and asked in words otherwise
    meetingLinkFields: [
        'קישור לאישור / שינוי / ביטול הפגישה',
        'קישור לאישור הפגישה',
        'קישור לשינוי מועד הפגישה',
        'קישור לביטול הפגישה',
    ],


    //the section the popups of a message about a meeting append (summon, message templates, meeting automations)
    meetingLinkSections() {

        return [{ title: 'אישור / שינוי / ביטול פגישה', fields: this.meetingLinkFields }];
    },


    //{ token: link } of one meeting - {} when there is none or the server says no
    meetingLinksOf(meetingId) {

        if (!meetingId)
            return Promise.resolve({});

        return axiosV2.get('meeting-response/links/' + meetingId)
            .then(response => (response && response.data && response.data.links) || {})
            .catch(() => ({}));
    },


    //does the text use one of the meeting's links?
    usesMeetingLinkFields(text) {

        let found = false;

        String(text || '').replace(/{{(.*?)}}/g, (_, fieldName) => {

            if (this.meetingLinkFields.indexOf(this.cleanFieldName(fieldName)) > -1)
                found = true;
        });

        return found;
    },


    //the 'תיאום פגישות' section every הוסף פרמטר popup appends - [] for accounts without online booking,
    //so the parameter shows up in the same place everywhere or nowhere at all
    bookingFieldSections() {

        let user = (window.planup && window.planup.$store.getters.getUser) || {};

        if ((user.bizServices || []).map(String).indexOf('10') == -1)
            return [];

        return [{ title: 'תיאום פגישות', fields: this.bookingFields }];
    },


    //builds a {fieldName: actualValue} map from whatever context we have.
    //any of meeting/event/customer may be null - missing fields resolve to ''
    buildFieldMappings(meeting, event, customer) {

        let mappings = {};

        customer = customer || (event && event.customer) || (meeting && meeting.customer) || null;

        let contacts = (customer && customer.contacts) || (meeting && meeting.customer && meeting.customer.contacts) || [];

        mappings['שם הלקוח'] = contacts.map(contact => ((contact.firstName || '') + ' ' + (contact.lastName || '')).trim()).filter(name => name).join(', ');

        //no single recipient here - the customer's first contact (per-recipient send paths
        //override both with the recipient's own name)
        let firstContact = contacts.find(contact => contact && (contact.firstName || contact.lastName)) || null;
        mappings[this.contactFirstNameField] = (firstContact && firstContact.firstName ? String(firstContact.firstName) : '').trim();
        mappings[this.contactLastNameField] = (firstContact && firstContact.lastName ? String(firstContact.lastName) : '').trim();

        let contactWithPhone = contacts.find(contact => contact.phone);
        mappings['טלפון הלקוח'] = (contactWithPhone && contactWithPhone.phone) || '';

        let contactWithEmail = contacts.find(contact => contact.email);
        mappings['אימייל הלקוח'] = (contactWithEmail && contactWithEmail.email) || '';

        mappings['התאריך של היום'] = planup.$globals.niceDate(new Date());

        //the plain tenant booking link. send flows that know WHO the recipient is swap in a
        //personalised one (invite code, form opens pre-filled) when they fill the token on the
        //server - SummonsService/CustomersService/AutomationMarkersService
        mappings['קישור לזימון פגישה'] = Config.bookingLink(planup.$store.state.tenantId);

        if (meeting) {
            mappings['שם הפגישה'] = meeting.title || '';
            mappings['סוג הפגישה'] = (meeting.meetingType && meeting.meetingType.name) || '';
            mappings['מקום הפגישה'] = (meeting.place && meeting.place.name) || '';
            mappings['תאריך הפגישה'] = meeting.date ? planup.$globals.niceDate(new Date(meeting.date)) : '';
            mappings['יום הפגישה בשבוע'] = meeting.date ? planup.$globals.dayOfWeekHebrew(new Date(meeting.date)) : '';
            mappings['שעת התחלת הפגישה'] = meeting.time || '';
            mappings['שעת סיום הפגישה'] = meeting.endTime || '';
            mappings['שמות אנשי הקשר בפגישה'] = contacts.map(contact => contact.firstName).join(', ');
            mappings['שמות אנשי הצוות בפגישה'] = (meeting.crewMembers || []).map(member => member.name).join(', ');
        }

        if (event) {
            let eventContacts = (event.customer && event.customer.contacts) || contacts;
            mappings['שם האירוע'] = event.name || '';
            mappings['סוג האירוע'] = (event.eventClass && event.eventClass.name) || '';
            mappings['מקום האירוע'] = (event.place && event.place.name) || '';
            mappings['תאריך האירוע'] = event.date ? planup.$globals.niceDate(new Date(event.date)) : '';
            mappings['יום האירוע בשבוע'] = event.date ? planup.$globals.dayOfWeekHebrew(new Date(event.date)) : '';
            mappings['שעת התחלת האירוע'] = event.time || '';
            mappings['שעת סיום האירוע'] = event.endTime || '';
            mappings['מספר אורחים באירוע'] = event.estimatedNumberOfGuests ? '' + event.estimatedNumberOfGuests : '';
            mappings['שמות אנשי הקשר באירוע'] = eventContacts.map(contact => contact.firstName).join(', ');
            mappings['שמות אנשי הצוות באירוע'] = (event.crewMembers || []).map(member => member.name).join(', ');
        }

        return mappings;
    },


    //replaces {{parameter}} placeholders in text/html, same idea as the server engines.
    //unknown fields become '' so no raw {{...}} ever reaches a saved summary
    fillTemplate(text, mappings) {

        if (!text)
            return text;

        return text.replace(/{{(.*?)}}/g, (_, fieldName) => {

            return mappings[this.cleanFieldName(fieldName)] || '';
        });
    },


    //ckeditor html may have &nbsp; inside a hand-typed token, and text pasted
    //from whatsapp/word may carry invisible direction/zero-width chars
    cleanFieldName(fieldName) {

        return fieldName.replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ').replace(/[\u200E\u200F\u200B\uFEFF]/g, '').trim();
    },


    //does the text use any {{parameter}} of a meeting (date, time, name, its links...)?
    usesMeetingFields(text) {

        let found = false;

        String(text || '').replace(/{{(.*?)}}/g, (_, fieldName) => {

            let name = this.cleanFieldName(fieldName);

            if (this.meetingFields.indexOf(name) > -1 || this.meetingLinkFields.indexOf(name) > -1)
                found = true;
        });

        return found;
    },


    //the customer's next meeting - the nearest one that hasn't started yet - for a message
    //template sent from a place that has no meeting of its own (the WhatsApp chat):
    //"נקבעה עבורך פגישה ב{{תאריך הפגישה}}" is about that meeting.
    //null when the customer has none ahead (or the lookup fails)
    nextMeetingOfCustomer(customerId) {

        if (!customerId)
            return Promise.resolve(null);

        return axiosV2.get('meeting/getMeetings/' + customerId).then(response => {

            let now = new Date();
            let next = null, nextStart = null;

            (response.data || []).forEach(meeting => {

                if (!meeting || !meeting.date || meeting.hidden)
                    return;

                //meetings are stored at noon - the real start is the time string
                let start = new Date(meeting.date);
                let time = /^(\d{1,2}):(\d{2})/.exec(meeting.time || '');
                if (time)
                    start.setHours(+time[1], +time[2], 0, 0);
                else
                    start.setHours(23, 59, 0, 0);

                if (start < now || (nextStart && start >= nextStart))
                    return;

                next = meeting;
                nextStart = start;
            });

            return next;

        }).catch(() => null);
    },

}
